import { NavLink, useLocation } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  UsersIcon,
  DocumentTextIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../../lib/utils';
import { useUIStore } from '../../stores';
import { useIsDesktop } from '../../lib/hooks/useMediaQuery';
import { useClerkOpsEnabled } from '../../features/clerk-ops';
import { APP_NAME } from '@/stores/partner.store';

const navigation = [
  { name: 'Conversations', href: '/dashboard', icon: ChatBubbleLeftRightIcon },
  { name: 'Team Chat', href: '/team-chat', icon: ChatBubbleOvalLeftEllipsisIcon },
  { name: 'Staff', href: '/staff', icon: UsersIcon },
  { name: 'Analytics', href: '/analytics', icon: ChartBarIcon },
  { name: 'Customers', href: '/customers', icon: UserGroupIcon },
  { name: 'Knowledge Base', href: '/knowledge', icon: DocumentTextIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
];

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  // Tier-3: only the Faxter-Clerk tenant sees the Operations console nav item.
  const { data: clerkOpsEnabled } = useClerkOpsEnabled();
  const items = clerkOpsEnabled
    ? [
        ...navigation.slice(0, 4),
        { name: 'Clerk Ops', href: '/clerk-ops', icon: BriefcaseIcon },
        ...navigation.slice(4),
      ]
    : navigation;
  // An open conversation lives at /conversations/:sessionId, still under Conversations.
  const inConversation = useLocation().pathname.startsWith('/conversations/');
  return (
    <>
      {/* Logo */}
      <div className="flex h-16 items-center gap-2.5 border-b border-line px-6">
        <span className="flex h-8 w-8 items-center justify-center rounded-[10px] bg-gradient-to-br from-brand to-brand-ink shadow-s">
          <svg
            viewBox="0 0 24 24"
            className="h-[18px] w-[18px]"
            fill="none"
            stroke="#fff"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M2 12h4l2.5-7 4 14 3-9 2 2h4" />
          </svg>
        </span>
        <span className="font-display text-xl font-bold tracking-tight text-ink">{APP_NAME}</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-3 py-4">
        {items.map((item) => {
          const forceActive = item.href === '/dashboard' && inConversation;
          return (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.href === '/dashboard'}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive || forceActive
                    ? 'bg-brand/10 text-brand'
                    : 'text-ink-2 hover:bg-black/[0.04] hover:text-ink'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={cn(
                      'h-5 w-5 flex-shrink-0',
                      isActive || forceActive ? 'text-brand' : 'text-ink-3 group-hover:text-ink-2'
                    )}
                  />
                  {item.name}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-line p-4">
        <div className="text-xs text-ink-3">
          <p>Version 1.0.0</p>
          <p className="mt-1">Powered by AI</p>
        </div>
      </div>
    </>
  );
}

export default function Sidebar() {
  const { sidebarOpen, setSidebarOpen } = useUIStore();
  const isDesktop = useIsDesktop();

  if (!sidebarOpen) return null;

  // Desktop: inline sidebar
  if (isDesktop) {
    return (
      <div className="flex w-64 flex-col border-r border-line bg-surface">
        <SidebarContent />
      </div>
    );
  }

  // Mobile: overlay with backdrop
  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink/30" onClick={() => setSidebarOpen(false)} />
      <div className="fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-surface shadow-l">
        <SidebarContent onNavigate={() => setSidebarOpen(false)} />
      </div>
    </>
  );
}
